import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Enhanced ScrollToTop component for React Router v6
 * - Automatically scrolls to top on route changes
 * - Handles hash links by scrolling to target element
 * - Detects scrollable containers and scrolls appropriately
 * - Provides smooth scrolling behavior
 */
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  // Function to find scrollable container
  const getScrollableContainer = () => {
    const commonSelectors = [
      '.app-content',
      '#root',
      '.main',
      '.content',
      '.page-content',
      '[data-scroll-container]'
    ];
    
    for (const selector of commonSelectors) {
      const element = document.querySelector(selector);
      if (element && element.scrollHeight > element.clientHeight) {
        return element;
      }
    }
    
    return window;
  };

  // Function to scroll to top
  const scrollToTop = (container = window) => {
    if (container === window) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      container.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Function to scroll to hash element
  const scrollToHash = (hash, container = window) => {
    if (!hash) return;

    const element = document.querySelector(hash);
    if (element) {
      // Element found, scroll to it
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Element not found, scroll to top
      scrollToTop(container);
    }
  };

  useEffect(() => {
    const container = getScrollableContainer();
    
    // Debug logging
    console.log('ScrollToTop: Route changed', { pathname, hash, container: container === window ? 'window' : container.className });
    
    // Small delay to ensure DOM is updated
    const timeoutId = setTimeout(() => {
      if (hash) {
        // Hash present, try to scroll to element
        console.log('ScrollToTop: Scrolling to hash', hash);
        scrollToHash(hash, container);
      } else {
        // No hash, scroll to top
        console.log('ScrollToTop: Scrolling to top');
        scrollToTop(container);
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [pathname, hash]);

  return null;
}

export default ScrollToTop;
